/**
 * "Add <store>" in one click (Data tab, editors): the services of the project that can use the store (all of them by
 * default, the only one when there is one), a line saying what is written, then Add. Writes the store's small
 * declaration file in each chosen service (vfs-service POST /scaffold/store: committed changes, undone from History) and
 * offers to start or restart those services, so the store comes up. A service that already declares the store is shown
 * as such and is not touched.
 */
import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { CheckCircle } from '@phosphor-icons/react';
import { DataDialog, ErrorState, Loading, errorText } from './DataCommon';
import { addStore } from '../../services/DataService';
import { restartService, sessionIdFor, startService } from '../../services/SandboxService';
import { openHistory } from '../../utils/workspaceModel';
import { kindLabel, serviceLabel } from '../../utils/dataModel';

const names = (list) => list.map(serviceLabel).join(', ');

/** What Add writes, in one line: the file(s) of the first chosen service, and where else. */
export function previewLine(kind, chosen) {
  if (!chosen.length) { return 'Pick at least one service.'; }
  const file = chosen[0].files.map((f) => f.slice(f.indexOf('/', f.indexOf('/') + 1) + 1)).join(' + ');
  return `Writes ${file} in ${names(chosen.map((c) => c.service))}.`;
}

export default function AddStoreDialog({ projectId, kind, runningServices = [], knownServices = [], onAdded, onClose }) {
  const label = kindLabel(kind);
  const [plan, setPlan] = useState(null); // dry run: [{ service, files, already }]
  const [error, setError] = useState(null);
  const [picked, setPicked] = useState(null); // Set of service paths
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null); // { services: [paths written] }
  const [restart, setRestart] = useState({ busy: false, message: null });

  useEffect(() => {
    let live = true;
    addStore(projectId, { kind, dryRun: true }).then((answer) => {
      if (!live) { return; }
      setPlan(answer.services || []);
      setPicked(new Set((answer.services || []).filter((s) => !s.already).map((s) => s.service)));
    }).catch((e) => { if (live) { setError(errorText(e)); } });
    return () => { live = false; };
  }, [projectId, kind]);

  const free = useMemo(() => (plan || []).filter((s) => !s.already), [plan]);
  const chosen = free.filter((s) => picked?.has(s.service));
  const toggle = (service) => setPicked((prev) => {
    const next = new Set(prev);
    if (next.has(service)) { next.delete(service); } else { next.add(service); }
    return next;
  });

  const submit = async() => {
    setBusy(true);
    setError(null);
    try {
      const answer = await addStore(projectId, { kind, services: chosen.map((c) => c.service) });
      setDone({ services: answer.services.filter((s) => s.written.length).map((s) => s.service) });
      onAdded?.(answer);
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };

  const restartAll = async() => {
    setRestart({ busy: true, message: null });
    const failures = [];
    for (const service of done.services) {
      try {
        // eslint-disable-next-line no-await-in-loop
        if (knownServices.includes(service)) { await restartService(sessionIdFor(projectId, service)); } else { await startService(projectId, service); }
      } catch (e) {
        failures.push(`${serviceLabel(service)}: ${errorText(e)}`);
      }
    }
    setRestart({ busy: false, message: failures.length ? failures.join(' · ') : `Started ${names(done.services)}: ${label} comes up with it.` });
  };

  if (done) {
    const running = done.services.filter((s) => runningServices.includes(s));
    return (
      <DataDialog
        footer={(
          <>
            <button className="data-btn" onClick={onClose} type="button">Close</button>
            <button className="data-btn is-primary" data-autofocus data-testid="data-add-restart" disabled={restart.busy || !done.services.length} onClick={restartAll} type="button">
              {restart.busy ? 'Starting…' : running.length ? `Restart ${names(done.services)}` : `Start ${names(done.services)}`}
            </button>
          </>
        )}
        onClose={onClose}
        testId="data-add-dialog"
        title={`${label} added`}
      >
        <p className="data-footnote" data-testid="data-add-done">
          <CheckCircle aria-hidden size={14} /> {done.services.length ? `${label} is set up in ${names(done.services)}.` : `${label} was already there: nothing changed.`}{' '}
          {kind === 'postgres' || kind === 'mysql' ? 'It starts with the first service that uses it. ' : ''}
          It comes up the next time {done.services.length === 1 ? 'that service starts' : 'those services start'}.{' '}
          <button className="data-link" onClick={() => { openHistory({ tab: 'changes' }); onClose(); }} type="button">Undo from History</button>
        </p>
        {restart.message && <p className="data-footnote" data-testid="data-add-restarted" role="status">{restart.message}</p>}
      </DataDialog>
    );
  }

  return (
    <DataDialog
      footer={(
        <>
          <button className="data-btn" onClick={onClose} type="button">Cancel</button>
          <button className="data-btn is-primary" data-testid="data-add-confirm" disabled={busy || !chosen.length} onClick={submit} type="button">
            {busy ? 'Adding…' : `Add ${label}`}
          </button>
        </>
      )}
      onClose={onClose}
      subtitle="Which services use it?"
      testId="data-add-dialog"
      title={`Add ${label}`}
    >
      {error && <ErrorState error={error} />}
      {!plan && !error && <Loading label="Looking at the services…" />}
      {plan && (
        <div className="data-add-services" role="group" aria-label="Services">
          {plan.map((s) => (
            <label className="data-add-service" key={s.service}>
              {s.already
                ? <input checked disabled readOnly type="checkbox" />
                : <input checked={picked.has(s.service)} data-autofocus={s === free[0] ? true : undefined} onChange={() => toggle(s.service)} type="checkbox" />}
              <span>{serviceLabel(s.service)}</span>
              {s.already && <small>already uses {label}</small>}
            </label>
          ))}
          <p className="data-footnote" data-testid="data-add-preview">{free.length ? previewLine(kind, chosen) : `Every service already uses ${label}.`}</p>
        </div>
      )}
    </DataDialog>
  );
}

AddStoreDialog.propTypes = {
  kind: PropTypes.string.isRequired,
  knownServices: PropTypes.arrayOf(PropTypes.string),
  onAdded: PropTypes.func,
  onClose: PropTypes.func.isRequired,
  projectId: PropTypes.string.isRequired,
  runningServices: PropTypes.arrayOf(PropTypes.string),
};
